import React, { useState, useEffect } from 'react';
import { GitCompareArrows, AlertCircle } from 'lucide-react';

const StockDifference = () => {
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [tanks, setTanks] = useState([]);
  const [tankId, setTankId] = useState('');

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Load the list of physical tanks once, so each one (e.g. Diesel 1 / Diesel 2) is its own option
  useEffect(() => {
    fetch('/api/config', { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } })
      .then(res => res.json())
      .then(cfg => {
        const t = cfg.tanks || [];
        setTanks(t);
        if (t.length > 0) setTankId(String(t[0].id));
      })
      .catch(err => console.error('Error loading tanks:', err));
  }, []);

  useEffect(() => {
    if (!tankId) return;
    const fetchData = async () => {
      setIsLoading(true);
      setError('');
      try {
        const res = await fetch(`/api/reports/stock-difference-data?month=${month}&year=${year}&tankId=${tankId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        const json = await res.json();
        if (json.error) {
          setError(json.error);
          setData(null);
        } else {
          setData(json);
        }
      } catch (err) {
        setError(err.message);
        setData(null);
      }
      setIsLoading(false);
    };
    fetchData();
  }, [month, year, tankId]);

  const fmt = (v) => (v === null || v === undefined) ? '-' : Number(v).toFixed(2);
  const dayOf = (dateStr) => new Date(dateStr).getDate();

  const renderTank = (tankBlock) => {
    const { tank, nozzles, rows } = tankBlock;
    let totalDiff = 0;
    let knownDays = 0;
    rows.forEach(r => { if (r.difference !== null) { totalDiff += r.difference; knownDays += 1; } });

    return (
      <div key={tank.id} className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-800">{tank.tank_name}</h2>
          <span className="text-xs text-slate-500">Fed by: {nozzles.length > 0 ? nozzles.map(n => `${n.pump_name} · N${n.nozzle_number}`).join(', ') : 'no nozzles mapped to this tank'}</span>
        </div>
        <div className="overflow-x-auto border border-slate-200 rounded-xl shadow-sm bg-white">
          <table className="w-full text-sm text-left border-collapse min-w-[800px]">
            <thead className="bg-slate-50 text-slate-700 font-bold uppercase text-xs">
              <tr className="border-b border-slate-200">
                <th className="p-3 border-r border-slate-200 text-center">Date</th>
                <th className="p-3 border-r border-slate-200 text-center">Opening Dip</th>
                <th className="p-3 border-r border-slate-200 text-center">Receipt</th>
                <th className="p-3 border-r border-slate-200 text-center">Testing (share)</th>
                <th className="p-3 border-r border-slate-200 text-center">Net Sale</th>
                <th className="p-3 border-r border-slate-200 text-center bg-blue-50/50">Expected Closing</th>
                <th className="p-3 border-r border-slate-200 text-center bg-blue-100/50">Actual Closing (Dip)</th>
                <th className="p-3 text-center">Difference</th>
              </tr>
            </thead>
            <tbody className="text-slate-600 font-medium">
              {rows.map(row => {
                const diff = row.difference;
                const diffClass = diff === null ? 'text-slate-400' : diff < -0.5 ? 'text-red-600 bg-red-50/50 font-bold' : diff > 0.5 ? 'text-emerald-600 bg-emerald-50/50 font-bold' : 'text-slate-500';
                return (
                  <tr key={row.date} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="p-2 border-r border-slate-200 text-center font-semibold text-slate-800">{dayOf(row.date)}</td>
                    <td className="p-2 border-r border-slate-200 text-right">{fmt(row.openingDip)}</td>
                    <td className="p-2 border-r border-slate-200 text-right">{fmt(row.receipt)}</td>
                    <td className="p-2 border-r border-slate-200 text-right text-slate-400">{fmt(row.testing)}</td>
                    <td className="p-2 border-r border-slate-200 text-right">{fmt(row.sales)}</td>
                    <td className="p-2 border-r border-slate-200 text-right bg-blue-50/30">{fmt(row.expectedClosing)}</td>
                    <td className="p-2 border-r border-slate-200 text-right bg-blue-100/30 font-bold">{fmt(row.physicalClosing)}</td>
                    <td className={`p-2 text-right ${diffClass}`}>{diff === null ? '-' : (diff > 0 ? '+' : '') + diff.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-amber-50 font-bold text-slate-900 border-t-2 border-slate-300">
                <td colSpan={7} className="p-3 border-r border-slate-200 text-right">TOTAL DIFFERENCE ({knownDays} days known)</td>
                <td className={`p-3 text-right ${totalDiff < -0.5 ? 'text-red-600' : totalDiff > 0.5 ? 'text-emerald-600' : 'text-slate-700'}`}>
                  {(totalDiff > 0 ? '+' : '') + totalDiff.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8 max-w-7xl mx-auto pb-12">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <GitCompareArrows className="w-6 h-6 text-indigo-600" /> Stock Difference
          </h1>
          <p className="text-slate-500 text-sm mt-1">Physical (dip) stock vs. what the books say it should be, per tank, per day.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select className="input-field py-2 bg-white" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {Array.from({length: 12}, (_, i) => i + 1).map(m => (
              <option key={m} value={m}>{new Date(0, m - 1).toLocaleString('default', { month: 'long' })}</option>
            ))}
          </select>
          <input type="number" className="input-field py-2 w-24 bg-white" value={year} onChange={(e) => setYear(Number(e.target.value))} />
          <select className="input-field py-2" value={tankId} onChange={(e) => setTankId(e.target.value)}>
            {tanks.map(t => (
              <option key={t.id} value={t.id}>{t.tank_name}</option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5" />
          <p className="font-medium">{error}</p>
        </div>
      )}

      {isLoading ? (
        <div className="p-12 text-center text-slate-500 font-medium animate-pulse">Reconciling dip readings against meter sales...</div>
      ) : (
        data && data.tanks.map(renderTank)
      )}
    </div>
  );
};

export default StockDifference;
